// Sound.
//
// Split the same way the results panel is: `soundsFor` is a pure function from two snapshots of the
// run to a list of sounds, and `AudioKit` is a thin synthesiser. What makes a noise is therefore
// unit-testable without Web Audio, which is the half that actually goes wrong — a sound that fires
// every tick instead of on a change is the audio equivalent of announcing the clock.
//
// SYNTHESISED, NOT SAMPLED. No files to load, nothing for a strict CSP to block, no decode latency
// on the first bark, and the whole thing is a few hundred bytes of code rather than a few hundred
// kilobytes of assets. It also cannot drift out of sync with the palette the way an asset would.
//
// **Nothing here touches the simulation.** Audio is driven from the render callback off sim state;
// it never feeds anything back, so determinism and replay are unaffected by whether sound is on.

export type Sound = 'command' | 'lift' | 'bark' | 'penned' | 'panic' | 'win'

/** The bits of run state a sound can key off. Deliberately tiny — snapshots are taken every frame. */
export interface SoundSnapshot {
  barks: number
  penned: number
  order: string | null
  lifted: boolean
  panicking: number
  won: boolean
}

/**
 * Which sounds a frame should play.
 *
 * Every rule is an EDGE, never a level. Panic in particular: keying on "any sheep is panicking"
 * would retrigger on every frame of a long fright, so it fires when the flock goes from settled to
 * frightened and not again until it has settled.
 */
export function soundsFor(prev: SoundSnapshot, next: SoundSnapshot): Sound[] {
  const out: Sound[] = []
  if (next.barks > prev.barks) out.push('bark')
  if (next.order !== prev.order) out.push('command')
  if (next.lifted && !prev.lifted) out.push('lift')
  if (next.penned > prev.penned) out.push('penned')
  if (next.panicking > 0 && prev.panicking === 0) out.push('panic')
  if (next.won && !prev.won) out.push('win')
  return out
}

const MUTE_KEY = 'bigdog.muted.v1'

/**
 * A small Web Audio synth.
 *
 * The context is created lazily on the first user gesture, because iOS refuses to start one any
 * other way and a context created at load simply sits suspended — which reads as "audio is broken"
 * rather than "audio has not been permitted yet".
 */
export class AudioKit {
  private ctx: AudioContext | null = null
  private muted: boolean

  constructor(private readonly store: { getItem(k: string): string | null; setItem(k: string, v: string): void } | null) {
    // Guarded, not just the write. Safari private browsing throws on ACCESS, so an unguarded read
    // here would take the whole app down at construction — the exact failure the stats adapter was
    // written to avoid, missed once already.
    let stored: string | null = null
    try {
      stored = store?.getItem(MUTE_KEY) ?? null
    } catch {
      stored = null
    }
    this.muted = stored === '1'
  }

  get isMuted(): boolean {
    return this.muted
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    try {
      this.store?.setItem(MUTE_KEY, muted ? '1' : '0')
    } catch {
      // A full or unavailable localStorage must not stop the game making noise, or silence it.
    }
  }

  /** Call from a real user gesture. Safe to call repeatedly. */
  unlock(): void {
    if (this.muted || this.ctx) return
    try {
      const Ctor =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      void this.ctx.resume()
    } catch {
      this.ctx = null // no audio available; the game does not depend on it
    }
  }

  play(sound: Sound): void {
    if (this.muted || !this.ctx) return
    const t = this.ctx.currentTime
    switch (sound) {
      // A bark is the loudest thing here and the only one with any grit: a short noise burst under a
      // low blip. It has to cut through, because it is the one action with a score cost.
      case 'bark':
        this.blip(150, 0.16, 'square', 0.16, t)
        this.noise(0.12, 0.1, t)
        break
      // The rest are quiet and short. This is a patient game; a chirpy interface would fight it.
      case 'command':
        this.blip(620, 0.05, 'sine', 0.05, t)
        break
      case 'lift':
        this.blip(500, 0.07, 'sine', 0.05, t)
        this.blip(760, 0.09, 'sine', 0.05, t + 0.07)
        break
      case 'penned':
        this.blip(880, 0.16, 'sine', 0.07, t)
        break
      case 'panic':
        this.noise(0.22, 0.05, t)
        break
      case 'win':
        this.blip(523, 0.16, 'sine', 0.08, t)
        this.blip(659, 0.16, 'sine', 0.08, t + 0.12)
        this.blip(784, 0.3, 'sine', 0.08, t + 0.24)
        break
    }
  }

  private blip(freq: number, dur: number, type: OscillatorType, gain: number, when: number): void {
    const ctx = this.ctx
    if (!ctx) return
    const osc = ctx.createOscillator()
    const amp = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(freq, when)
    // Ramp rather than a hard stop: a gain that jumps to zero clicks, and a click on every command
    // would be more noticeable than the sound itself.
    amp.gain.setValueAtTime(0, when)
    amp.gain.linearRampToValueAtTime(gain, when + 0.008)
    amp.gain.exponentialRampToValueAtTime(0.0001, when + dur)
    osc.connect(amp).connect(ctx.destination)
    osc.start(when)
    osc.stop(when + dur + 0.02)
  }

  private noise(dur: number, gain: number, when: number): void {
    const ctx = this.ctx
    if (!ctx) return
    const frames = Math.max(1, Math.floor(ctx.sampleRate * dur))
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1
    const src = ctx.createBufferSource()
    src.buffer = buffer
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(1100, when)
    const amp = ctx.createGain()
    amp.gain.setValueAtTime(gain, when)
    amp.gain.exponentialRampToValueAtTime(0.0001, when + dur)
    src.connect(filter).connect(amp).connect(ctx.destination)
    src.start(when)
    src.stop(when + dur)
  }
}
